import React, { useState, useEffect, useCallback, useRef } from "react";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import WhatsAppStylePopup from "./WhatsAppStylePopup";
import { playNotificationSound } from "@/utils/notificationSound";

/**
 * Global Real-Time Notification Listener
 * --------------------------------------
 * Listens for WebSocket "notification" events and shows WhatsApp-style
 * message popups (top-right) to the user they are relevant to.
 *
 * - workflow_popup (purchase request / transaction workflow):
 *     • GMD          → requests & transactions requiring his approval
 *     • Procurement  → requests awaiting procurement review
 *     • Finance      → requests awaiting finance approval
 *     • Managers     → requests awaiting their approval
 *     • Requesters   → status changes on their own requests
 * - manager_action_required (legacy event from managerNotificationService)
 *
 * Popups are filtered against the current user's permissions / user type so
 * everybody only sees the popups related to them.
 *
 */

// Permission needed to see a popup for a given target role
const TARGET_ROLE_PERMISSION = {
  gmd: "gmd-dashboard",
  procurement: "purchase-procurement",
  finance: "purchase-finance",
  manager: "purchase-pending",
};

const MAX_POPUPS = 3;
const POPUP_DURATION = 8000;

const RealTimeNotificationListener = () => {
  const { socket, isConnected } = useWebSocket();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [popups, setPopups] = useState([]);

  // Current employee id (used to determine whether an incoming message is for us)
  const currentEmployeeId = user?.Employee?.id || user?.employee?.id || null;

  const popupsEnabledRef = useRef(true);

  /**
   * Decide whether a popup targeting a role is relevant to the current user.
   */
  const isRelevantToUser = useCallback(
    (targetRole) => {
      if (!targetRole || targetRole === "requester") return true;

      // Strictly permission-gated: if you don't have access to the page a
      // role's notifications are about, you don't see them - no fallback.
      // This used to fall through to a UserType-name keyword match (e.g.
      // "includes('gmd')") when the real permission check failed, which
      // could show GMD/procurement/finance popups to someone whose UserType
      // name happened to contain the word but who doesn't actually hold the
      // permission. Every role emitted today (gmd/procurement/finance/
      // manager) has a real permission mapped below, so there's nothing left
      // for a fallback to cover - an unmapped role fails closed (hidden)
      // rather than defaulting to shown-to-everyone.
      const permissionId = TARGET_ROLE_PERMISSION[targetRole];
      if (!permissionId) return false;
      return hasPermission(permissionId);
    },
    [hasPermission],
  );

  const addPopup = useCallback(
    (data, options = {}) => {
      // Don't collect popups when the user disabled them entirely
      if (!popupsEnabledRef.current) return;
      setPopups((prev) => {
        const next = [...prev, { id: Date.now() + Math.random(), ...data }];
        return next.slice(-MAX_POPUPS);
      });
      if (options.playSound !== false) {
        playNotificationSound();
      }
    },
    [playNotificationSound],
  );

  const removePopup = useCallback((id) => {
    setPopups((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handleOpen = useCallback(
    (popup) => {
      if (popup.url) navigate(popup.url);
    },
    [navigate],
  );

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNotification = (data) => {
      // WhatsApp-style workflow popups (from workflowRealtimeService)
      if (data?.type === "workflow_popup") {
        if (!isRelevantToUser(data.targetRole)) return;
        addPopup(data);
        return;
      }

      // Legacy manager_action_required events.
      // The backend already targets the exact manager userId, so always show.
      if (data?.type === "manager_action_required") {
        const info = data.data || {};
        addPopup({
          popupType: "manager_action_required",
          targetRole: "manager",
          title: "Action Required",
          message: info.message || "A new item requires your attention",
          sender: "ERP System",
          amount: info.amount || null,
          requestId: info.request_number || info.request_id || null,
          url: info.url || "/dashboard",
          priority: "high",
          icon: "megaphone",
          createdAt: new Date().toISOString(),
        });
        return;
      }
    };

    // notification:new is emitted by notificationService.emitRealtime() for
    // every persisted notification (messages, GMD, procurement, system, etc.).
    // Show an in-app WhatsApp-style popup AND update the notification bell.
    const handleNotificationNew = (data) => {
      if (!data) return;
      const category = data.category || "system";

      // Direct messages already get a popup via the direct_message:new socket
      // event (see handler below). Skip to avoid showing two popups for the
      // same message.
      if (category === "messages") return;

      addPopup({
        popupType: category,
        targetRole: "requester",
        title: data.title || "New Notification",
        message: data.message || "",
        sender: "ERP System",
        amount: null,
        requestId: data.reference_id || data.id || null,
        url: data.action_url || "/dashboard",
        priority: data.priority || "normal",
        icon: category === "messages" ? "message" : "bell",
        createdAt: data.created_at || new Date().toISOString(),
      });
    };

    socket.on("notification", handleNotification);
    socket.on("notification:new", handleNotificationNew);
    return () => {
      socket.off("notification", handleNotification);
      socket.off("notification:new", handleNotificationNew);
    };
  }, [socket, isConnected, addPopup, isRelevantToUser]);

  /**
   * Collaboration (direct messaging) WhatsApp-style popups.
   * Shows when a new direct message arrives for the current user.
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleDirectMessage = (data) => {
      if (!data) return;

      // Only show popups for messages sent TO the current user
      if (data.from_employee_id === currentEmployeeId) return;
      if (data.to_employee_id && data.to_employee_id !== currentEmployeeId)
        return;

      const messageText =
        data.message ||
        (data.file_url ? "📎 Shared a file" : "Sent you a message");

      // Same notification sound as GMD/procurement popups - fires regardless
      // of which page the user is on, including the messaging page itself.
      addPopup({
        popupType: "direct_message",
        targetRole: "requester",
        title: "New Message",
        message: messageText,
        sender: data.from_employee_name || "Team Member",
        amount: null,
        requestId: data.message_id || data.id || null,
        url: "/collaboration/messaging",
        priority: "normal",
        icon: "message",
        createdAt: data.created_at || new Date().toISOString(),
      });
    };

    socket.on("direct_message:new", handleDirectMessage);
    return () => {
      socket.off("direct_message:new", handleDirectMessage);
    };
  }, [socket, isConnected, addPopup, currentEmployeeId]);

  /**
   * Collaboration (thread messaging) WhatsApp-style popups.
   * Shows when a new thread message arrives for the current user.
   */
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleThreadMessage = (data) => {
      if (!data) return;
      const message = data.message || {};
      const sender = message.sender || {};

      // Don't popup for messages the current user sent
      if (message.sender_id === currentEmployeeId) return;

      const senderName =
        sender.name ||
        `${sender.first_name || ""} ${sender.last_name || ""}`.trim() ||
        "Team Member";
      const content =
        message.content ||
        (message.attachments?.length
          ? "📎 Shared an attachment"
          : "Sent a message");

      addPopup({
        popupType: "thread_message",
        targetRole: "requester",
        title: "New Message",
        message: content,
        sender: senderName,
        amount: null,
        requestId: message.id || null,
        url: data.threadId
          ? `/collaboration/messaging/${data.threadId}`
          : "/collaboration/messaging",
        priority: "normal",
        icon: "message",
        createdAt: message.created_at || new Date().toISOString(),
      });
    };

    socket.on("message:new", handleThreadMessage);
    return () => {
      socket.off("message:new", handleThreadMessage);
    };
  }, [socket, isConnected, addPopup, currentEmployeeId]);

  // Only render once a user is logged in (hides the control on public pages
  // like the login screen, where no popups can arrive anyway)
  if (!user) return null;

  return (
    <div className="fixed top-20 right-4 z-[9999] flex flex-col items-end gap-3 pointer-events-none">
      {/* Popup stack - z-60 keeps popups clickable above the settings backdrop */}
      {popups.map((popup) => (
        <div key={popup.id} className="pointer-events-auto relative z-[60]">
          <WhatsAppStylePopup
            popup={popup}
            duration={POPUP_DURATION}
            onClose={() => removePopup(popup.id)}
            onOpen={handleOpen}
          />
        </div>
      ))}
    </div>
  );
};

export default RealTimeNotificationListener;
